import type { ReactElement, MouseEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { DISCIPLINE_COLORS } from '@/lib/disciplineColors';
import { disciplineLabel } from '@/features/dashboard/WorkoutCard';
import type { CalendarItem } from './calendarItems';

/**
 * Chip compacto de UN entreno en una celda Mes/Semana (M2.29 — antes
 * recibía `ActualWorkout`; ahora recibe `CalendarItem` que añade el
 * concepto de ESTADO: planned, completed, skipped, partial, actual_unplanned).
 *
 * Estilo visual por estado:
 *  - `completed` → fondo emerald-500 (verde) + ✓.
 *  - `planned`   → outline color disciplina (border-l-4), fondo blanco,
 *                  texto color disciplina-700. Sin emoji.
 *  - `skipped`   → fondo slate-100, texto slate-400 line-through + ✗.
 *  - `partial`   → fondo amber-500, texto blanco + ½.
 *  - `actual_unplanned` → fondo lleno color disciplina, texto blanco
 *                         (igual que antes para Strava sync sin planificar).
 *
 * Click → si hay `actualId` navega a `/entreno/$id`; si solo hay planned
 * (futuro), por ahora también navega a /entrenos (no hay vista detalle
 * de planned como tal todavía). stopPropagation para no disparar el
 * click del cell padre que cambia a vista Día.
 */
export function DayChip({
  item,
  compact = true,
}: {
  item: CalendarItem;
  compact?: boolean;
}): ReactElement {
  const navigate = useNavigate();
  const color = DISCIPLINE_COLORS[item.discipline];
  const minutes = Math.round(item.durationMin);
  const km =
    typeof item.distanceM === 'number' && item.distanceM > 0
      ? item.distanceM / 1000
      : null;

  const distLabel = km !== null ? `${formatKm(km)} km` : null;
  const label = compact
    ? distLabel
      ? `${minutes}m · ${distLabel}`
      : `${minutes}m`
    : distLabel
      ? `${minutes} min · ${distLabel}`
      : `${minutes} min`;

  function handleClick(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    if (item.actualId !== null) {
      void navigate({ to: '/entreno/$id', params: { id: String(item.actualId) } });
    }
    // Si sólo es planned (sin actual aún), no navegamos a ningún detalle —
    // el click del cell se ocupará de llevar a vista Día.
  }

  // Estilo según kind ------------------------------------------------------
  const baseChip =
    'block w-full truncate rounded-md px-1.5 py-0.5 text-left text-[11px] font-medium hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1';

  if (item.kind === 'completed') {
    return (
      <button
        type="button"
        onClick={handleClick}
        data-testid={`day-chip-${item.key}`}
        data-kind="completed"
        aria-label={`${disciplineLabel(item.discipline)} completado, ${minutes} minutos${distLabel ? `, ${distLabel}` : ''}. Ver detalle`}
        className={`${baseChip} bg-emerald-500 text-white`}
        title="Completado"
      >
        <span aria-hidden="true">✓ </span>
        {label}
      </button>
    );
  }

  if (item.kind === 'partial') {
    return (
      <button
        type="button"
        onClick={handleClick}
        data-testid={`day-chip-${item.key}`}
        data-kind="partial"
        aria-label={`${disciplineLabel(item.discipline)} parcial, ${minutes} minutos. Ver detalle`}
        className={`${baseChip} bg-amber-500 text-white`}
        title="Parcial"
      >
        <span aria-hidden="true">½ </span>
        {label}
      </button>
    );
  }

  if (item.kind === 'skipped') {
    return (
      <button
        type="button"
        onClick={handleClick}
        data-testid={`day-chip-${item.key}`}
        data-kind="skipped"
        aria-label={`${disciplineLabel(item.discipline)} saltado, ${minutes} minutos planificados`}
        className={`${baseChip} bg-slate-100 text-slate-400 line-through`}
        title="Saltado"
      >
        <span aria-hidden="true">✗ </span>
        {label}
      </button>
    );
  }

  // M2.41 — Planned vencido sin actual vinculado (datetime < now).
  // Visualmente rojo + icono ⚠ para distinguirlo claramente de 'skipped'
  // (explícito) y 'planned' (futuro). El click sigue navegando a /entreno/$id
  // si por alguna razón hay actualId (no debería en este estado, pero defensivo).
  if (item.kind === 'missed') {
    return (
      <button
        type="button"
        onClick={handleClick}
        data-testid={`day-chip-${item.key}`}
        data-kind="missed"
        aria-label={`${disciplineLabel(item.discipline)} no realizado, ${minutes} minutos planificados`}
        className={`${baseChip} bg-rose-500 text-white`}
        title="No realizado (vencido sin actual vinculado)"
      >
        <span aria-hidden="true">⚠ </span>
        {label}
      </button>
    );
  }

  if (item.kind === 'planned') {
    // Outline color disciplina + fondo blanco para distinguir de "hecho".
    return (
      <button
        type="button"
        onClick={handleClick}
        data-testid={`day-chip-${item.key}`}
        data-kind="planned"
        aria-label={`${disciplineLabel(item.discipline)} planificado, ${minutes} minutos`}
        style={{ borderLeftColor: color.hex }}
        className={`${baseChip} border border-slate-200 border-l-4 bg-white text-slate-800`}
        title="Planificado (pendiente)"
      >
        {label}
      </button>
    );
  }

  // M2.34.C — Comidas planificadas (fondo amber). Click sin navegación por
  // ahora (la receta vinculada se podrá abrir desde la timeline detallada
  // en futuras iteraciones; aquí evitamos navegar a `/entreno/$id`).
  if (item.kind === 'meal') {
    const mealTitle =
      item.title && item.title.trim().length > 0 ? item.title : 'Comida';
    return (
      <button
        type="button"
        onClick={(e) => e.stopPropagation()}
        data-testid={`day-chip-${item.key}`}
        data-kind="meal"
        aria-label={`Comida planificada: ${mealTitle}, ${minutes} minutos`}
        className={`${baseChip} border border-amber-400 bg-amber-100 text-amber-900`}
        title={`Comida: ${mealTitle}`}
      >
        <span aria-hidden="true">🍽 </span>
        {truncate(mealTitle, 22)}
      </button>
    );
  }

  // M2.34.C — Prep tasks (fondo sky azul claro).
  if (item.kind === 'prep') {
    const prepTitle =
      item.title && item.title.trim().length > 0
        ? item.title.replace(/^Preparar\s+/i, '')
        : 'Prep';
    return (
      <button
        type="button"
        onClick={(e) => e.stopPropagation()}
        data-testid={`day-chip-${item.key}`}
        data-kind="prep"
        aria-label={`Preparación: ${prepTitle}, ${minutes} minutos`}
        className={`${baseChip} border border-sky-400 bg-sky-100 text-sky-900`}
        title={`Prep: ${prepTitle}`}
      >
        <span aria-hidden="true">⏳ </span>
        Prep: {truncate(prepTitle, 18)}
      </button>
    );
  }

  // actual_unplanned — fondo lleno color disciplina (Strava sin plan).
  const dark = item.discipline === 'bike';
  const textClass = dark ? 'text-slate-900' : 'text-white';
  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid={`day-chip-${item.key}`}
      data-kind="actual_unplanned"
      aria-label={`${disciplineLabel(item.discipline)} sin planificar, ${minutes} minutos${distLabel ? `, ${distLabel}` : ''}. Ver detalle`}
      style={{ backgroundColor: color.hex }}
      className={`${baseChip} ${textClass}`}
      title="Sin planificar (Strava)"
    >
      {label}
    </button>
  );
}

function formatKm(km: number): string {
  if (km >= 100) return Math.round(km).toString();
  return km.toFixed(1);
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + '…';
}
